import { useEffect, useMemo, useState } from "react";

import { fetchStockDetail, updateStock } from "../services/marketDataRemote";

function firstDefined(...values) {
  return values.find((value) => value !== null && value !== undefined && value !== "");
}

function formatDate(date) {
  return date.toISOString().slice(0, 10);
}

function buildDefaultDateRange() {
  const dateTo = new Date();
  const dateFrom = new Date(dateTo);
  dateFrom.setDate(dateTo.getDate() - 89);

  return {
    dateFrom: formatDate(dateFrom),
    dateTo: formatDate(dateTo),
  };
}

function formatPrice(value) {
  const price = Number(value);
  if (!Number.isFinite(price)) return "-";

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);
}

function formatNumber(value, digits = 0) {
  if (value == null || value === "") return "-";
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(digits) : value ?? "-";
}

function formatPercent(value, digits = 0) {
  const formatted = formatNumber(value, digits);
  return formatted === "-" ? "-" : `${formatted}%`;
}

function normalizeDateOnly(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function parseDateValue(value) {
  if (!value) return null;
  if (value instanceof Date) return normalizeDateOnly(value);

  const text = String(value).trim();
  const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return normalizeDateOnly(new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3])));
  }

  const formattedMatch = text.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{2,4})$/);
  if (formattedMatch) {
    const month = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"].indexOf(
      formattedMatch[2].toLowerCase()
    );
    if (month >= 0) {
      const yearValue = Number(formattedMatch[3]);
      const year = yearValue < 100 ? 2000 + yearValue : yearValue;
      return normalizeDateOnly(new Date(year, month, Number(formattedMatch[1])));
    }
  }

  return normalizeDateOnly(new Date(text));
}

function filterRowsByCalendarDays(rows = [], dateTo, days) {
  const endDate = parseDateValue(dateTo) ?? normalizeDateOnly(new Date());
  if (!endDate) return [];

  const startDate = new Date(endDate);
  startDate.setDate(endDate.getDate() - (days - 1));

  return rows.filter((row) => {
    const rowDate = parseDateValue(row?.mktDate ?? row?.marketDate ?? row?.Date);
    return rowDate && rowDate >= startDate && rowDate <= endDate;
  });
}

function buildPerformanceStats(rows = []) {
  return rows.reduce(
    (stats, row) => {
      const type = String(row?.tradeType ?? row?.performance ?? "").toUpperCase();
      if (type.includes("POSITIVE") || type.includes("GAIN") || type.includes("UP")) stats.positive += 1;
      else if (type.includes("NEGATIVE") || type.includes("LOSS") || type.includes("DOWN")) stats.negative += 1;
      else stats.flat += 1;
      return stats;
    },
    { positive: 0, negative: 0, flat: 0 }
  );
}

function getActionClass(action) {
  const normalized = String(action ?? "").toUpperCase();
  if (normalized.includes("BUY")) return "action-buy";
  if (normalized.includes("SELL")) return "action-sell";
  if (normalized.includes("AVOID")) return "action-avoid";
  return "action-watch";
}

function getConfidenceClass(value) {
  const confidence = Number(value);
  if (!Number.isFinite(confidence)) return "confidence-low";
  if (confidence >= 90) return "confidence-high";
  if (confidence >= 70) return "confidence-medium";
  return "confidence-low";
}

function formatDecisionText(value) {
  return String(value ?? "-").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
}

function getTradeabilityTone(value) {
  const normalized = String(value ?? "").toUpperCase();
  if (normalized === "ENTRY_READY") return "ready";
  if (normalized === "AVOID_INTRADAY") return "avoid";
  if (normalized === "FAILED" || normalized === "STALE") return "stale";
  return "watch";
}

function resolveStockId(stock) {
  return firstDefined(stock?.ticker_id, stock?.tickerId, stock?.stockId, stock?.symbol, stock?.ticker);
}

function mapPointValue(point, key) {
  const value = point?.[key];
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function buildLinePath(points, valueKey, xForIndex, yForValue) {
  return points
    .map((point, index) => {
      const value = mapPointValue(point, valueKey);
      if (value === null) return null;
      return `${index === 0 ? "M" : "L"} ${xForIndex(index).toFixed(2)} ${yForValue(value).toFixed(2)}`;
    })
    .filter(Boolean)
    .join(" ");
}

function MiniLineChart({
  data,
  lines,
  xKey,
  yMin,
  yMax,
  referenceLines = [],
  emptyText,
  xTickCount = 3,
  showAllXTicks = false,
  showPointLabels = false,
}) {
  const width = 720;
  const height = 360;
  const padding = { top: 26, right: 66, bottom: 42, left: 54 };
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;
  const validData = Array.isArray(data) ? data : [];

  const values = validData.flatMap((point) =>
    lines.map((line) => mapPointValue(point, line.key)).filter((value) => value !== null)
  );
  referenceLines.forEach((line) => {
    const value = Number(line.value);
    if (Number.isFinite(value)) values.push(value);
  });

  const computedMin = Number.isFinite(Number(yMin)) ? Number(yMin) : Math.min(...values);
  const computedMax = Number.isFinite(Number(yMax)) ? Number(yMax) : Math.max(...values);
  const rangePadding = computedMin === computedMax ? 1 : (computedMax - computedMin) * 0.08;
  const min = Number.isFinite(computedMin) ? computedMin - rangePadding : 0;
  const max = Number.isFinite(computedMax) ? computedMax + rangePadding : 1;
  const span = max - min || 1;

  const xForIndex = (index) => padding.left + (validData.length <= 1 ? 0 : (index / (validData.length - 1)) * plotWidth);
  const yForValue = (value) => padding.top + plotHeight - ((value - min) / span) * plotHeight;
  const yTicks = Array.from({ length: 5 }, (_, index) => min + (span / 4) * index);
  const resolvedXTickCount = Math.max(2, Math.min(Number(xTickCount) || 3, validData.length || 2));
  const xTicks = validData.length
    ? showAllXTicks
      ? validData.map((_, index) => index)
      : Array.from({ length: resolvedXTickCount }, (_, index) =>
          Math.round((index / (resolvedXTickCount - 1)) * (validData.length - 1))
        ).filter((value, index, items) => items.indexOf(value) === index)
    : [];

  if (!validData.length || !values.length) {
    return <div className="stock-chart-empty">{emptyText}</div>;
  }

  return (
    <svg className="stock-chart-svg" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={emptyText}>
      <rect x="0" y="0" width={width} height={height} rx="8" />
      {yTicks.map((tick) => {
        const y = yForValue(tick);
        return (
          <g key={tick}>
            <line className="chart-grid-line" x1={padding.left} x2={width - padding.right} y1={y} y2={y} />
            <text className="chart-axis-label" x={padding.left - 10} y={y + 4} textAnchor="end">
              {formatNumber(tick, 0)}
            </text>
          </g>
        );
      })}
      {xTicks.map((tickIndex) => {
        const point = validData[tickIndex];
        const x = xForIndex(tickIndex);
        return (
          <g key={tickIndex}>
            <line className="chart-grid-line chart-grid-line-vertical" x1={x} x2={x} y1={padding.top} y2={height - padding.bottom} />
            <text className="chart-axis-label" x={x} y={height - 14} textAnchor="middle">
              {point?.[xKey] ?? ""}
            </text>
          </g>
        );
      })}
      {referenceLines.map((line) => {
        const value = Number(line.value);
        if (!Number.isFinite(value)) return null;
        const y = yForValue(value);
        return (
          <g key={line.label}>
            <line className="chart-reference-line" x1={padding.left} x2={width - padding.right} y1={y} y2={y} stroke={line.color} />
            <text className="chart-reference-label" x={width - padding.right + 8} y={y + 4} fill={line.color}>
              {line.label}
            </text>
          </g>
        );
      })}
      {lines.map((line) => (
        <path
          className="stock-chart-line"
          d={buildLinePath(validData, line.key, xForIndex, yForValue)}
          key={line.key}
          stroke={line.color}
        />
      ))}
      {showPointLabels && lines.map((line, lineIndex) => (
        <g key={`${line.key}-points`}>
          {validData.map((point, index) => {
            const value = mapPointValue(point, line.key);
            if (value === null) return null;
            const x = xForIndex(index);
            const y = yForValue(value);
            const labelOffset = lineIndex % 2 === 0 ? -9 : 15;
            return (
              <g key={`${line.key}-${point?.[xKey] ?? index}`}>
                <circle className="chart-point-marker" cx={x} cy={y} r="3.2" fill={line.color} />
                <text
                  className="chart-point-label"
                  x={x}
                  y={y + labelOffset}
                  fill={line.color}
                  textAnchor="middle"
                >
                  {formatNumber(value, 0)}
                </text>
              </g>
            );
          })}
        </g>
      ))}
      <line className="chart-axis-line" x1={padding.left} x2={width - padding.right} y1={height - padding.bottom} y2={height - padding.bottom} />
      <line className="chart-axis-line" x1={padding.left} x2={padding.left} y1={padding.top} y2={height - padding.bottom} />
    </svg>
  );
}

function CompactMetric({ label, value, className = "" }) {
  return (
    <div className={`compact-detail-metric ${className}`}>
      <span>{label}</span>
      <strong>{value ?? "-"}</strong>
    </div>
  );
}

function CompactMetricRow({ label, value }) {
  return (
    <div className="compact-metric-row">
      <span>{label}</span>
      <strong>{value ?? "-"}</strong>
    </div>
  );
}

function DetailSection({ title, meta, defaultOpen = false, children }) {
  return (
    <details className="stock-detail-accordion" defaultOpen={defaultOpen}>
      <summary>
        <span>{title}</span>
        {meta && <strong>{meta}</strong>}
      </summary>
      <div className="stock-detail-accordion-body">{children}</div>
    </details>
  );
}

function StockDetailSkeleton({ label = "Loading stock detail" }) {
  return (
    <div className="stock-detail-skeleton" role="status" aria-live="polite">
      <span>{label}</span>
      <div />
      <div />
      <div />
    </div>
  );
}

function mergeStockDetail(stock, detail) {
  return {
    tickerId: firstDefined(detail?.ticker_id, detail?.tickerId, stock?.symbol),
    companyName: firstDefined(detail?.company_name, detail?.companyName, stock?.name),
    rank: firstDefined(detail?.rank, stock?.rank),
    listingDate: firstDefined(detail?.fmt_listing_date, detail?.listingDate),
    sector: firstDefined(detail?.sector, stock?.sector),
    industry: firstDefined(detail?.industry, stock?.industry),
    prevClose: firstDefined(stock?.liveClose, detail?.prev_close, detail?.previousClose),
    open: firstDefined(stock?.liveOpen, detail?.open_price, detail?.open),
    high: firstDefined(stock?.liveHigh, detail?.high, detail?.dayHigh),
    low: firstDefined(stock?.liveLow, detail?.low, detail?.dayLow),
    close: firstDefined(stock?.liveLtp, detail?.price, stock?.price),
    high52: firstDefined(detail?.high52, detail?.week52High),
    low52: firstDefined(detail?.low52, detail?.week52Low),
    chartMin: detail?.chartMin,
    chartMax: detail?.chartMax,
    positiveDays: firstDefined(detail?.positive_days, 0),
    negativeDays: firstDefined(detail?.negative_days, 0),
    parallelDays: firstDefined(detail?.parallel_days, 0),
    marketSentiment: firstDefined(detail?.market_sentiment, "Neutral"),
    noInvestment: detail?.no_investment_zone === 1 || detail?.noInvestmentZone === true,
    nizComments: firstDefined(detail?.niz_comments, detail?.nizComments, ""),
  };
}

export default function StockDetailPage({
  stock,
  onBack,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
  onRefreshTradeability,
  refreshingTradeability = false,
}) {
  const [dateRange] = useState(buildDefaultDateRange);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [noInvestment, setNoInvestment] = useState(false);
  const [noInvestmentComment, setNoInvestmentComment] = useState("");
  const [previousNizComment, setPreviousNizComment] = useState("");
  const [savingNoInvestment, setSavingNoInvestment] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [decisionStatus, setDecisionStatus] = useState(null);

  const stockId = resolveStockId(stock);
  const actionClass = getActionClass(stock?.action);
  const confidenceClass = getConfidenceClass(stock?.confidenceScore);

  useEffect(() => {
    if (!stockId) return;

    let ignore = false;
    setLoadingDetail(true);
    setDetailError("");
    setDecisionStatus(null);

    fetchStockDetail(stockId, dateRange.dateFrom, dateRange.dateTo)
      .then((data) => {
        if (ignore) return;
        setDetail(data);
        const nizFlag = data?.no_investment_zone === 1 || data?.noInvestmentZone === true;
        const comments = data?.niz_comments ?? data?.nizComments ?? "";
        setNoInvestment(nizFlag);
        setNoInvestmentComment(comments);
        setPreviousNizComment(comments);
        setHasUnsavedChanges(false);
      })
      .catch((error) => {
        if (ignore) return;
        console.warn("Unable to load stock detail", error);
        setDetail(null);
        setDetailError("Stock chart data is unavailable. Showing advisor pick details only.");
      })
      .finally(() => {
        if (!ignore) setLoadingDetail(false);
      });

    return () => {
      ignore = true;
    };
  }, [dateRange.dateFrom, dateRange.dateTo, stockId]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!hasUnsavedChanges) return;
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const display = useMemo(() => mergeStockDetail(stock, detail), [detail, stock]);
  const tradeabilityTone = getTradeabilityTone(stock?.tradeabilityStatus);
  const tradeabilityChecks = Array.isArray(stock?.checks) ? stock.checks : [];
  const tradeabilityReasons = Array.isArray(stock?.reasons) ? stock.reasons : [];
  const advisorNotesAvailable = Boolean(stock?.reason || stock?.whatToWatch || stock?.riskNote || tradeabilityReasons.length);
  const performanceRows = useMemo(
    () => filterRowsByCalendarDays(detail?.stockSentiment, dateRange.dateTo, 90),
    [dateRange.dateTo, detail?.stockSentiment]
  );
  const performanceStats = useMemo(() => buildPerformanceStats(performanceRows), [performanceRows]);

  if (!stock) return null;

  const saveNoInvestmentDecision = async () => {
    const comments = noInvestmentComment.trim();
    if (!comments) {
      setDecisionStatus({ tone: "warning", message: "Add a reason before saving this decision." });
      return;
    }

    setSavingNoInvestment(true);
    setDecisionStatus({ tone: "info", message: "Saving No Investment Zone decision..." });
    try {
      await updateStock(stockId, noInvestment, comments);
      setHasUnsavedChanges(false);
      const freshDetail = await fetchStockDetail(stockId, dateRange.dateFrom, dateRange.dateTo);
      setDetail(freshDetail);
      const freshComments = freshDetail?.niz_comments ?? freshDetail?.nizComments ?? comments;
      setPreviousNizComment(freshComments);
      setNoInvestmentComment(freshComments);
      setDecisionStatus({
        tone: "success",
        message: noInvestment ? "Stock marked as No Investment Zone." : "Stock removed from No Investment Zone.",
      });
    } catch (error) {
      console.error("Failed to update No Investment Zone", error);
      setDecisionStatus({ tone: "error", message: "Failed to update status. Try again." });
    } finally {
      setSavingNoInvestment(false);
    }
  };

  return (
    <section className={`stock-detail-page stock-chart-detail ${actionClass}`}>
      <div className="stock-detail-header compact-stock-header">
        <div className="stock-title-block">
          <div className="stock-title-line">
            <h2>{display.tickerId}</h2>
            <span className="stock-action-badge">{stock.action ?? "WATCH"}</span>
          </div>
          <p>{display.companyName}</p>
        </div>
        <div className="stock-detail-actions">
          <button
            className="secondary-action-btn"
            type="button"
            onClick={() => onRefreshTradeability?.(stock)}
            disabled={refreshingTradeability}
          >
            {refreshingTradeability ? "Refreshing from NSE..." : "Refresh via NSE"}
          </button>
          <button className="secondary-action-btn" type="button" onClick={onBack}>
            Back
          </button>
        </div>
      </div>

      {detailError && <div className="stock-detail-warning">{detailError}</div>}
      {decisionStatus && (
        <div className={`stock-detail-status status-${decisionStatus.tone}`} role="status" aria-live="polite">
          {decisionStatus.message}
        </div>
      )}

      <div className="compact-decision-strip">
        <CompactMetric label={stock?.liveLtp != null ? "NSE LTP" : "Price"} value={formatPrice(display.close)} />
        <CompactMetric label="Quote" value={formatDecisionText(stock.quoteStatus)} />
        <CompactMetric label="Confidence" value={formatPercent(stock.confidenceScore)} className={confidenceClass} />
        <CompactMetric label="Score" value={formatNumber(stock.score)} />
        <CompactMetric label="Tradeability" value={formatNumber(stock.tradeabilityScore)} className={`tradeability-metric-${tradeabilityTone}`} />
        <CompactMetric label="Live Status" value={formatDecisionText(stock.tradeabilityStatus)} className={`tradeability-metric-${tradeabilityTone}`} />
        <CompactMetric label="NIZ" value={noInvestment ? "Avoid" : "Allowed"} className={noInvestment ? "niz-active" : "niz-clear"} />
      </div>

      {(stock?.tradeabilityStatus || tradeabilityChecks.length || tradeabilityReasons.length) && (
        <DetailSection title="Tradeability Decision" meta={formatDecisionText(stock.tradeabilityStatus)} defaultOpen>
          <div className={`tradeability-detail-panel ${tradeabilityTone}`}>
            <div className="tradeability-detail-lead">
              <span>{formatDecisionText(stock.tradeabilityStatus)}</span>
              <strong>{formatNumber(stock.tradeabilityScore)} / 100</strong>
              <p>{tradeabilityReasons[0] ?? "Tradeability checks are derived from strategy fit, liquidity, volume quality, price structure, and entry trigger."}</p>
            </div>
            {tradeabilityChecks.length > 0 && (
              <div className="tradeability-check-grid">
                {tradeabilityChecks.map((check) => (
                  <div className={`tradeability-check ${String(check.status ?? "").toLowerCase()}`} key={check.checkName}>
                    <span>{check.checkName}</span>
                    <strong>{formatDecisionText(check.status)}</strong>
                    <small>{check.detail ?? formatNumber(check.score)}</small>
                  </div>
                ))}
              </div>
            )}
            {tradeabilityReasons.length > 1 && (
              <ul className="tradeability-reason-list">
                {tradeabilityReasons.slice(1).map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
            )}
          </div>
        </DetailSection>
      )}

      <div className="stock-chart-panel daily-panel compact-primary-chart">
        <div className="chart-panel-header">
          <h3>Daily Chart</h3>
          <span>{dateRange.dateFrom} to {dateRange.dateTo}</span>
        </div>
        {loadingDetail && !detail ? (
          <StockDetailSkeleton label="Loading daily price history" />
        ) : (
          <MiniLineChart
            data={detail?.chartData}
            xKey="Date"
            yMin={display.chartMin}
            yMax={display.chartMax}
            lines={[{ key: "Price", color: "#1f5fbf" }]}
            referenceLines={[
              { value: display.high52, label: `${formatNumber(display.high52, 0)} 52W High`, color: "#008f6b" },
              { value: display.low52, label: `${formatNumber(display.low52, 0)} 52W Low`, color: "#c2413a" },
            ]}
            emptyText="No daily chart data available."
            xTickCount={7}
          />
        )}
      </div>

      <DetailSection title="Overview" meta={display.sector} defaultOpen>
        <div className="compact-overview-list">
          <CompactMetricRow label="Sector" value={display.sector} />
          <CompactMetricRow label="Industry" value={display.industry} />
          <CompactMetricRow label="NSE Rank" value={display.rank} />
          <CompactMetricRow label="Listed On" value={display.listingDate} />
          <CompactMetricRow label="Previous Close" value={formatPrice(display.prevClose)} />
          <CompactMetricRow label="Open" value={formatPrice(display.open)} />
          <CompactMetricRow label="High" value={formatPrice(display.high)} />
          <CompactMetricRow label="Low" value={formatPrice(display.low)} />
        </div>
      </DetailSection>

      <DetailSection title="Risk Decision" meta={noInvestment ? "No Investment Zone" : "Allowed"}>
        <div className="niz-panel compact-niz-panel">
          <label>
            <input
              type="checkbox"
              checked={noInvestment}
              onChange={(event) => {
                const checked = event.target.checked;
                setNoInvestment(checked);
                setNoInvestmentComment(checked ? "" : previousNizComment);
                setHasUnsavedChanges(true);
              }}
            />
            Mark as No Investment Zone
          </label>
          <textarea
            className={hasUnsavedChanges && !noInvestmentComment.trim() ? "niz-alert-textarea" : ""}
            placeholder={noInvestment ? "Reason to avoid this stock. Required." : "Reason for allowing this stock again."}
            rows="3"
            value={noInvestmentComment}
            onChange={(event) => {
              setNoInvestmentComment(event.target.value);
              setHasUnsavedChanges(true);
            }}
          />
          <button
            className="niz-save-btn"
            type="button"
            onClick={saveNoInvestmentDecision}
            disabled={savingNoInvestment || !hasUnsavedChanges || loadingDetail}
          >
            {savingNoInvestment ? "Saving..." : "Save Decision"}
          </button>
        </div>
      </DetailSection>

      <DetailSection title="10Y Range" meta={loadingDetail ? "Loading" : "Yearly high/low"}>
        {loadingDetail && !detail ? (
          <StockDetailSkeleton label="Loading yearly range" />
        ) : (
          <MiniLineChart
            data={detail?.histChartData}
            xKey="year"
            lines={[
              { key: "max_high", color: "#008f6b" },
              { key: "min_low", color: "#c2413a" },
            ]}
            emptyText="No historical high and low data available."
            showAllXTicks
            showPointLabels
          />
        )}
      </DetailSection>

      <DetailSection title="Performance" meta={`${performanceRows.length} rows / 90 days`}>
        <div className="compact-performance-summary">
          <CompactMetric label="Positive" value={performanceStats.positive} />
          <CompactMetric label="Negative" value={performanceStats.negative} />
          <CompactMetric label="Flat" value={performanceStats.flat} />
          <CompactMetric label="Sentiment" value={display.marketSentiment} />
        </div>
        <div className="performance-table-wrap">
          <table className="performance-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Market Date</th>
                <th>Price</th>
                <th>Performance</th>
                <th>Volume</th>
                <th>Signal Category</th>
              </tr>
            </thead>
            <tbody>
              {loadingDetail && !detail ? (
                <tr>
                  <td colSpan="6">
                    <StockDetailSkeleton label="Loading performance rows" />
                  </td>
                </tr>
              ) : performanceRows.length ? (
                performanceRows.map((item, index) => (
                  <tr key={`${item.mktDate}-${index}`}>
                    <td>{index + 1}</td>
                    <td>{item.mktDate}</td>
                    <td>{formatPrice(item.price)}</td>
                    <td>{item.tradeType}</td>
                    <td>{item.ticVolume}</td>
                    <td>{item.category}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6">No performance data available for the last 90 calendar days.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </DetailSection>

      {advisorNotesAvailable && (
        <DetailSection title="Advisor Notes">
          <div className="stock-detail-notes compact-notes">
            {tradeabilityReasons.length > 0 && (
              <article>
                <span>Tradeability Reason</span>
                <p>{tradeabilityReasons.join(" ")}</p>
              </article>
            )}
            {stock.reason && (
              <article>
                <span>Why It Is Listed</span>
                <p>{stock.reason}</p>
              </article>
            )}
            {stock.whatToWatch && (
              <article>
                <span>What To Watch</span>
                <p>{stock.whatToWatch}</p>
              </article>
            )}
            {stock.riskNote && (
              <article>
                <span>Risk Note</span>
                <p>{stock.riskNote}</p>
              </article>
            )}
          </div>
        </DetailSection>
      )}

      <div className="stock-detail-bottom-nav" aria-label="Stock detail navigation">
        <button type="button" onClick={onPrevious} disabled={!hasPrevious}>
          Previous
        </button>
        <button type="button" onClick={onBack}>
          List
        </button>
        <button type="button" onClick={onNext} disabled={!hasNext}>
          Next
        </button>
      </div>
    </section>
  );
}
